import type { DbPortalZeitreiheEintrag } from '@gql/shared-types';
import { JAHR_MAX, JAHR_MIN, KERN_BIS, KERN_VON, formatiereMonat, segmente } from '../daten/zeitreihe.js';
import { KATEGORIE_STIL } from '../farben.js';

const BREITE = 1000;
const LINKS = 24;
const RECHTS = 24;
const BALKEN_Y = 22;
const BALKEN_H = 26;

function x(jahr: number): number {
  return LINKS + ((jahr - JAHR_MIN) / (JAHR_MAX + 1 - JAHR_MIN)) * (BREITE - LINKS - RECHTS);
}

interface Props {
  zeitreihe: DbPortalZeitreiheEintrag[];
  namen: Map<string, string>;
}

export function KonzessionaerZeitstrahl({ zeitreihe, namen }: Props) {
  const seg = segmente(zeitreihe);
  const jahre: number[] = [];
  for (let j = JAHR_MIN; j <= JAHR_MAX + 1; j += 2) jahre.push(j);
  const beschreibung = zeitreihe
    .map((e) => `ab ${formatiereMonat(e.von)}: ${e.k.map((k) => namen.get(k) ?? k).join(', ') || 'kein Konzessionär erkannt'}`)
    .join('; ');

  return (
    <figure className="zeitstrahl">
      <div className="zeitstrahl-rahmen">
      <svg viewBox={`0 0 ${BREITE} 78`} role="img" aria-label={`Konzessionär im Zeitverlauf: ${beschreibung || 'keine Fassung bekannt'}`}>
        <defs>
          <pattern id="schraffur" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="transparent" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.55" />
          </pattern>
        </defs>
        <rect
          x={x(KERN_VON)}
          y={4}
          width={x(KERN_BIS + 1) - x(KERN_VON)}
          height={BALKEN_Y + BALKEN_H + 4 - 4}
          fill="#f5f5f4"
        />
        <text x={x(KERN_VON) + 4} y={15} className="zeitstrahl-kern">
          Kernzeitraum {KERN_VON}–{KERN_BIS}
        </text>
        <rect x={x(JAHR_MIN)} y={BALKEN_Y} width={x(JAHR_MAX + 1) - x(JAHR_MIN)} height={BALKEN_H} fill="#e7e5e4" rx={4} />
        {seg.map((s) => {
          const stil = KATEGORIE_STIL[s.kategorie];
          const x0 = x(s.von) + 1;
          const w = Math.max(x(s.bis) - x(s.von) - 2, 1);
          const text = `${s.konzessionaere.map((k) => namen.get(k) ?? k).join(', ') || 'kein Konzessionär erkannt'}${
            s.uebernommen ? ' (aus Vorfassung übernommen)' : ''
          }${s.offenesEnde ? ' – letzte bekannte Fassung' : ''}`;
          return (
            <g key={s.fassungId}>
              <title>{text}</title>
              <rect
                x={x0}
                y={BALKEN_Y}
                width={w}
                height={BALKEN_H}
                rx={4}
                fill={stil.fuellung === '#ffffff' ? '#ffffff' : stil.fuellung}
                stroke={stil.fuellung === '#ffffff' ? stil.rand : 'none'}
                strokeWidth={1.5}
                opacity={s.offenesEnde ? 0.55 : 1}
              />
              {s.uebernommen && <rect x={x0} y={BALKEN_Y} width={w} height={BALKEN_H} rx={4} fill="url(#schraffur)" />}
            </g>
          );
        })}
        {jahre.map((j) => (
          <g key={j}>
            <line x1={x(j)} x2={x(j)} y1={BALKEN_Y + BALKEN_H} y2={BALKEN_Y + BALKEN_H + 5} stroke="#a8a29e" />
            <text x={x(j)} y={BALKEN_Y + BALKEN_H + 18} textAnchor="middle" className="zeitstrahl-jahr">
              {j <= JAHR_MAX ? j : ''}
            </text>
          </g>
        ))}
      </svg>
      </div>
      <figcaption className="hinweis-klein">
        Balken = Gültigkeit der TAB-Fassungen (Standdatum bis zur nächsten Fassung). Blass: über die letzte bekannte
        Fassung hinaus fortgeschrieben. Schraffiert: Konzessionär aus der Vorfassung übernommen, weil die Fassung nicht
        ausgewertet werden konnte.
      </figcaption>
    </figure>
  );
}
